/**
 * 기사 로그인 — 휴대폰 번호 → 문자 인증번호(안 오면 카카오톡). 한 화면에 한 질문.
 * 아이디도 비밀번호도 캡차도 없다(60대 기사에게 신호등 캡차는 사실상 로그인 차단).
 */

import { useEffect, useRef, useState } from 'react';
import { useDriverAuth } from '../lib/driverAuth';
import { driverError } from '../lib/driverApi';
import { phone as fmtPhone } from './format';
import { Icon } from './icons';

/**
 * 안드로이드 앱의 인증번호 문자 자동 채움(native/driver-android `SmsCodePlugin` · 앱 1.2 부터).
 * 원격 WebView 는 autocomplete="one-time-code" 를 안 채운다(10-15 way 실기기) → 앱이 문자를 받아(시스템 「허용」 창) 6자리를 넘긴다.
 * 🔴 분기는 OS 가 아니라 플러그인이 있나로 — 옛 앱(1.1)·웹·아이폰은 그대로 손입력/키보드 제안.
 */
type SmsCodePlugin = { start: () => Promise<{ code: string }>; stop: () => Promise<void> };
const smsCode = (): SmsCodePlugin | undefined =>
  (window as unknown as { Capacitor?: { Plugins?: { SmsCode?: SmsCodePlugin } } }).Capacitor?.Plugins?.SmsCode;

/**
 * 안드로이드 크롬(웹)의 인증번호 자동 채움 = Web OTP(10-15 way 「기사 대부분이 안드로이드」).
 * 문자 맨 끝 줄 `@driver.busin.co.kr #코드`(functions `authSmsText`)를 크롬이 알아보고 「허용」 한 번으로 넘긴다.
 * 앱 WebView·아이폰·다른 주소(businweb.web.app 등)에선 없거나 안 오니 조용히 넘어간다.
 */
function webOtp(signal: AbortSignal): Promise<string> | null {
  if (typeof window === 'undefined' || !('OTPCredential' in window)) return null;
  return navigator.credentials
    .get({ otp: { transport: ['sms'] }, signal } as CredentialRequestOptions)
    .then((c) => (c as unknown as { code?: string } | null)?.code ?? '');
}

type Step = 'phone' | 'code';

export default function Login() {
  const { requestOtp, verifyOtp, evictedReason } = useDriverAuth();
  const [step, setStep] = useState<Step>('phone');
  const [digits, setDigits] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneValid = /^01\d{8,9}$/.test(digits);
  const listening = useRef(0);
  const otpAbort = useRef<AbortController | null>(null);

  function stopListening() {
    listening.current++;
    otpAbort.current?.abort();
    otpAbort.current = null;
    void smsCode()?.stop().catch(() => {});
  }

  useEffect(() => () => stopListening(), []);

  /** 문자를 보내기 **전에** 듣기 시작한다(듣기 시작 뒤에 온 문자만 잡힌다). 거절·시간 초과는 조용히 — 손으로 넣으면 된다. */
  function listenSms() {
    stopListening();
    const id = ++listening.current;
    const p = smsCode();
    let wait: Promise<string> | null;
    if (p) wait = p.start().then((r) => r.code);
    else {
      const ac = new AbortController();
      otpAbort.current = ac;
      wait = webOtp(ac.signal);
    }
    if (!wait) return;
    wait
      .then((c) => {
        if (id !== listening.current || !/^\d{6}$/.test(c)) return;
        setCode(c);
        void verify(c);
      })
      .catch(() => {});
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    listenSms();
    try {
      await requestOtp(digits);
      setStep('code');
    } catch (e) {
      setError(driverError(e, '문자를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setBusy(false);
    }
  }

  async function verify(c = code) {
    setBusy(true);
    setError(null);
    try {
      await verifyOtp(digits, c);
      // 성공하면 onAuthStateChanged 가 홈으로 전환한다.
    } catch (e) {
      setError(driverError(e, '인증에 실패했습니다.'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    // 스크롤 없이 한 화면(10-08 way 앱 실기기) — 높이는 dvh(앱·브라우저 바 뺀 실제 높이), 여백은 줄이고 아래 안내는 바닥에 붙인다.
    <main className="flex min-h-[100dvh] flex-col sm:min-h-[calc(100vh-3rem)]">
      <header className="driver-hero px-5 pb-12 pt-[calc(var(--sat)+1.75rem)] text-center text-white">
        <img src="/driver-icon-192.png" alt="" className="relative mx-auto h-16 w-16 rounded-[1.2rem] shadow-lg ring-1 ring-white/30" />
        <h1 className="relative mt-3 text-[1.75rem] font-bold">동영관광 기사앱</h1>
        <span aria-hidden className="relative mx-auto mt-2 block h-1 w-10 rounded-full bg-leaf" />
        <p className="relative mt-2 text-lg text-white/80">휴대폰 번호로 들어갑니다.</p>
      </header>

      <section className="relative -mt-8 flex flex-1 flex-col rounded-t-[28px] bg-surface px-5 pb-[calc(var(--sab)+1.25rem)] pt-6">

      {evictedReason && (
        <p className="mb-6 rounded-xl bg-brand-50 px-4 py-4 text-base text-ink">{evictedReason}</p>
      )}

      {step === 'phone' ? (
        <>
          <label htmlFor="phone" className="mb-2 block text-center text-lg font-semibold text-ink">
            휴대폰 번호
          </label>
          <input
            id="phone"
            className="driver-input text-center text-2xl tracking-wide"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="010-0000-0000"
            value={fmtPhone(digits)}
            onChange={(e) => setDigits(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))}
          />
          <p className="mt-3 break-keep text-center text-base text-ink-faint">
            회사에 등록된 번호로 <strong>문자</strong> 인증번호를 보내드립니다.
          </p>
          <button className="driver-btn mt-6" disabled={!phoneValid || busy} onClick={() => void sendCode()}>
            {busy ? '보내는 중…' : '인증번호 받기'}
          </button>
        </>
      ) : (
        <>
          <label htmlFor="code" className="mb-2 block text-center text-lg font-semibold text-ink">
            인증번호 6자리
          </label>
          <input
            id="code"
            className="driver-input text-center text-3xl tracking-widest"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            // 인증번호 화면으로 넘어오면 칸에 바로 커서(10-07 김길수 기사 「키보드가 안 뜬다」) — 카톡에서 돌아와도 칸을 다시 찾지 않게.
            autoFocus
            placeholder="000000"
            value={code}
            onChange={(e) => {
              // 6자리가 차면 바로 확인(10-15 way 「인증번호 입력을 어려워한다」) — 「확인」 버튼을 찾지 않게.
              const next = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
              setCode(next);
              if (next.length === 6 && next !== code && !busy) void verify(next);
            }}
          />
          <p className="mt-3 break-keep text-center text-base text-ink-faint">
            {fmtPhone(digits)} 번으로 <strong>문자</strong>를 보냈습니다. 5분 안에 입력해 주세요.
          </p>
          <button className="driver-btn mt-6" disabled={code.length !== 6 || busy} onClick={() => void verify()}>
            {busy ? '확인 중…' : '확인'}
          </button>
          <button
            className="mt-3 min-h-touch w-full text-base text-ink-soft underline"
            onClick={() => {
              stopListening();
              setStep('phone');
              setCode('');
              setError(null);
            }}
          >
            번호를 잘못 입력했어요
          </button>
        </>
      )}

      {error && <p className="mt-6 rounded-xl bg-red-50 px-4 py-4 text-base text-red-700">{error}</p>}

      <p className="mt-auto flex items-center justify-center gap-1.5 pt-6 text-base text-ink-faint">
        <Icon name="shield" className="h-5 w-5" />
        비밀번호 없이 인증번호로만 들어갑니다
      </p>
      </section>
    </main>
  );
}
